import React, {useEffect, useState, useTransition} from 'react';
import PropTypes from 'prop-types';
import {checkNullArr, checkNullJson, checkNullStr, getArrLen, getJsonValue, getDefStr, isBoolTrue, printError} from "../utils/ReactUtils";
import {Button, ConfirmDialog} from "../index";
import {CssVariant, THEME} from "../config/FusionConfig";

/**
 * Creates a multiple state toggle component.
 *
 * Implementation:
 *
 * @example
 * const data=[
 *      {
 *          text: 'Toggle 1',
 *          icon: 'toggle-icon',
 *          tooltip:'Tooltip 1',
 *          disabled: true/false,
 *          onClick:((text, index)=>{})
 *      },
 *      {
 *          text: 'Toggle 2',
 *          icon: 'toggle-icon2',
 *          tooltip:'Tooltip 1',
 *          disabled: true/false,
 *          onClick:((text, index)=>{})
 *      }
 * ];
 * <PolyStateToggle
 *         className={'mx-1'}
 *         variant={'primary'}
 *         bgVariant={'transparent'}
 *         rippleVariant={'light'}
 *         reverseVariant={'white'}
 *         itemClassName={''}
 *         itemIconClassName={''}
 *         defOption={0}
 *         fsVariant={'sm'}
 *         defOption={isGraphViewSelected ? 0 : 1}
 *         style={{}}
 *         itemStyle={{}}
 *     >{data}</PolyStateToggle>
 */
const PolyStateToggle = ({
                             className = "",
                             wrapperClassName = "",
                             itemClassName = '',
                             itemSelectedClassName = '',
                             itemIconClassName = '',
                             defOption = -1,
                             fsVariant = 'sm',
                             variant = THEME,
                             reverseVariant = 'white',
                             rippleVariant = 'light',
                             bgVariant = 't',
                             style = {},
                             itemStyle = {},
                             wrapperStyle = {},
                             textLessBtnW = '34px',
                             textLessBtnH = '',
                             height = '',
                             fontSize = '',

                             children,
                             hidden,
                             disabled = false,
                             textAsTooltip = false,
                             showText = true,
                             showSelectedText = false,
                             confirmBeforeSwitching = false,
                             onSwitch,
                             confirmSwitchTitle = "Confirm",
                             confirmSwitchBtnTitle = "Switch",
                             confirmSwitchMsg = "Are you sure you want to switch?",
                             confirmSwitchSubMsg = "",
                             confirmSwitchVariant = CssVariant.danger,
                             ...rest
                         }) => {
    const TAG = "PolyStateToggle";

    const [selectedIndex, setSelectedIndex] = useState(defOption);
    const [nextSelectDataTemp, setNextSelectDataTemp] = useState({});
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [switchTransitionPending, startSwitchTransition] = useTransition();

    useEffect(() => {
        try {
            let defIndexValue = -1;
            if (!isNaN(defOption) && Number(defOption) >= 0) {
                defIndexValue = Number(defOption);
            } else {
                // Find the index of the child whose "text" matches defOption
                const matchingChildIndex = children.findIndex(item => getJsonValue(item, 'text') == defOption);
                if (matchingChildIndex !== -1) {
                    defIndexValue = matchingChildIndex;
                } else {
                    defIndexValue = 0; // Set to 0 if not found
                }
            }
            setSelectedIndex(defIndexValue);
        } catch (e) {
            printError(TAG, e);
        }
    }, [children, defOption]);

    return (<>
        {!isBoolTrue(hidden) &&
            <div className={`m-0 p-0 ${wrapperClassName}`} style={wrapperStyle}>
                <div
                    className={`w-auto d-flex fd-row flex-nowrap justify-content-around align-items-center m-0 p-0 rounded-2 overflow-hidden border-${variant} border-solid border-1 bg-${bgVariant} ${className}`}
                    style={{...style}}>
                    {checkNullArr(children) && children.map((item, index) => {
                        const itemVariant = getDefStr(getJsonValue(item, 'variant'), variant);
                        const text = getJsonValue(item, 'text');
                        return (checkNullJson(item)) && <Button
                            key={index + text}
                            text={isBoolTrue(showText) ? text : ''}
                            tooltip={(isBoolTrue(textAsTooltip) && checkNullStr(text)) ? text : getJsonValue(item, 'tooltip')}
                            rounded={false}
                            width={(checkNullStr(text) && isBoolTrue(showText)) ? height : textLessBtnW}
                            height={(checkNullStr(text) && isBoolTrue(showText)) ? height : textLessBtnH}
                            disabled={isBoolTrue(item.disabled) || switchTransitionPending || isBoolTrue(disabled)}
                            className={`p-1 px-3 m-0 text-nowrap fw-semi-bold fs-${fsVariant} 
                            ${(selectedIndex !== index)
                                ? `bg-${bgVariant} highlight-${itemVariant}-hover text-${itemVariant}`
                                : `bg-${itemVariant} highlight-${itemVariant}-full-hover text-${reverseVariant}`
                            } ${itemClassName} ${selectedIndex == index ? itemSelectedClassName : ''}`}
                            fontSize={fontSize}
                            // style={{borderRight: index < getArrLen(children) - 1 ? `1px solid var(--${itemVariant})` : null, ...itemStyle}}
                            style={{borderRight: index < getArrLen(children) - 1 ? `1px solid var(--${variant})` : null, ...itemStyle}}
                            variant={''}
                            rippleColor={`var(--ripple-color-${selectedIndex !== index ? itemVariant : rippleVariant})`}
                            iconClass={(checkNullStr(item.icon) || checkNullStr(itemIconClassName)) ? `transition-lg fs-${fsVariant} 
                            text-${(selectedIndex !== index) ? itemVariant : reverseVariant} ${item.icon} ${itemIconClassName}` : null}
                            onClick={() => {
                                if (index != selectedIndex) {
                                    if (isBoolTrue(confirmBeforeSwitching)) {
                                        setNextSelectDataTemp({index, text, item});
                                        setShowConfirmDialog(true);
                                    } else {
                                        handleSwitch(index, text, item);
                                    }
                                }
                            }}
                            {...rest}
                        />
                    })}
                </div>
            </div>}
        {isBoolTrue(confirmBeforeSwitching) && createSwitchConfirmDialog()}
    </>)

    function handleSwitch(index, text, item) {
        startSwitchTransition(() => {
            setSelectedIndex(index);
            // printLog(TAG, 'index:', index, 'item:', item);
            if (item && item.onClick) item.onClick(text, index);
            if (onSwitch) onSwitch(text, index, item, item.id);
            setShowConfirmDialog(false);
        });
    }

    function createSwitchConfirmDialog() {
        return <ConfirmDialog
            open={showConfirmDialog}
            onHide={() => {
                setShowConfirmDialog(false);
                setNextSelectDataTemp({});
            }}
            closeOnEscape={false}
            onConfirm={() => {
                // printLog(TAG, 'createSwitchConfirmDialog.onConfirm.nextSelectDataTemp:', nextSelectDataTemp);
                handleSwitch(nextSelectDataTemp.index, nextSelectDataTemp.text, nextSelectDataTemp.item);
                setShowConfirmDialog(false);
                setNextSelectDataTemp({});
            }}
            title={confirmSwitchTitle}
            titleClassName={'text-gray fs-normal fw-semi-bold'}
            confirmText={confirmSwitchBtnTitle}
            // controlClassName={''}
            // confirmClassName={'text-white'}
            cancelClassName={'highlight-dark'}
            confirmVariant={confirmSwitchVariant}
            footerClassName={'my-1 mt-2'}
            contentClassName={"px-3"}
            enableEnter={false}
        >
            <div className={'m-0 p-0 w-100 pt-1 pb-2'}>
                <p className={'p-0 m-0 w-100 fs-normal text-black pt-1'}>
                    {confirmSwitchMsg}
                </p>
                {checkNullStr(confirmSwitchSubMsg) &&
                    <p className={'p-0 m-0 w-100 text-gray-dark pt-1 text-center'}>{confirmSwitchSubMsg}</p>}
            </div>
        </ConfirmDialog>
    }
}
PolyStateToggle.propTypes = {
    className: PropTypes.string,
    itemClassName: PropTypes.string,
    itemIconClassName: PropTypes.string,
    defOption: PropTypes.any,
    variant: PropTypes.string,
    reverseVariant: PropTypes.string,
    fsVariant: PropTypes.string,
    disabled: PropTypes.bool,
    rippleVariant: PropTypes.string,
    showSelectedText: PropTypes.bool,
    bgVariant: PropTypes.string,
    style: PropTypes.object,
    itemStyle: PropTypes.object,
    children: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string,
            icon: PropTypes.string,
            tooltip: PropTypes.string,
            onClick: PropTypes.func,
        })
    ).isRequired,
};
export default PolyStateToggle;
